import React, { useState, useMemo } from "react";
import Pagination from "./Pagination.jsx";
import CodeViewer from "./CodeViewer.jsx";
import "../UIcss/Title.css";
import { FaHeading } from "react-icons/fa";
import { LuCopy } from "react-icons/lu";
import { titleStylesData } from "./titleStylesData.js";

// --- GENERATOR LOGIC ---
const generateTitles = (count) => {
  const items = [];
  for (let i = 0; i < count; i++) {
    const className = i === 0 ? "compiler-title" : `compiler-title${i}`;
    items.push({
      id: `title-${i}`,
      index: i,
      className: className,
      text: "CodePulse-R",
      label: `Title Style ${i + 1}`,
      description: `Elegant style variation ${i + 1} with custom effects`,
    });
  }
  return items;
};

// --- RENDERER COMPONENT ---
const TitleRenderer = ({ config, onViewCode }) => {
  return (
    <div className="title-card" onClick={() => onViewCode(config)}>
      <div className="title-meta-header">
        <span className="title-label">{config.label}</span>
        <button
          className="title-action-btn"
          onClick={(e) => {
            e.stopPropagation();
            onViewCode(config);
          }}
          title="View Code"
        >
          <LuCopy />
        </button>
      </div>

      <div className="title-preview-area">
        <h1 className={config.className}>{config.text}</h1>
      </div>

      <div className="title-details-footer">
        <div className="title-desc-text">Variation {config.index + 1}</div>
      </div>
    </div>
  );
};

// --- HELPER TO GET CODE ---
const getGeneratedCode = (config) => {
  const htmlCode = `<!DOCTYPE html>\n<html lang="en">\n<head>\n    <meta charset="UTF-8">\n    <meta name="viewport" content="width=device-width, initial-scale=1.0">\n    <title>${config.label} - CodePulse UI</title>\n    <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700&family=Poppins:wght@400;700&family=Playfair+Display:ital,wght@0,400;1,400&family=Monoton&family=Press+Start+2P&family=VT323&display=swap" rel="stylesheet">\n    <style>\n        /* Paste the CSS from the CSS tab here */\n    </style>\n</head>\n<body>\n    <h1 class="${config.className}">${config.text}</h1>\n</body>\n</html>`;

  const fontImport = `@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;700&family=Playfair+Display:ital,wght@0,400;1,400&family=Monoton&family=Orbitron:wght@400;700&family=Press+Start+2P&family=VT323&display=swap');\n\n`;
  const dynamicCSS = titleStylesData[config.index]?.css || `.${config.className} { font-family: inherit; font-size: 2.5rem; color: #fff; }`;
  const cssCode = fontImport + dynamicCSS;

  return { jsxCode: htmlCode, cssCode };
};

const Title = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 40;
  const allTitles = useMemo(() => generateTitles(200), []);

  const [viewerState, setViewerState] = useState({
    isOpen: false,
    name: "",
    jsx: "",
    css: "",
  });

  const totalPages = Math.ceil(allTitles.length / itemsPerPage);

  const currentItems = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return allTitles.slice(indexOfFirstItem, indexOfLastItem);
  }, [currentPage, allTitles]);

  const handleOpenViewer = (config) => {
    const { jsxCode, cssCode } = getGeneratedCode(config);
    setViewerState({
      isOpen: true,
      name: config.label,
      jsx: jsxCode,
      css: cssCode,
    });
  };

  return (
    <div className="title-page-wrapper">
      <div className="library-header">
        <div className="header-content">
          <h1>
            <FaHeading /> Titles <span>Library</span>
          </h1>
        </div>
      </div>

      <div className="titles-grid">
        {currentItems.map((config) => (
          <TitleRenderer
            key={config.id}
            config={config}
            onViewCode={handleOpenViewer}
          />
        ))}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={allTitles.length}
        itemsPerPage={itemsPerPage}
      />

      <CodeViewer
        isOpen={viewerState.isOpen}
        onClose={() => setViewerState({ ...viewerState, isOpen: false })}
        componentName={viewerState.name}
        jsxCode={viewerState.jsx}
        cssCode={viewerState.css}
      />
    </div>
  );
};

export default Title;
